import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {of} from 'rxjs/observable/of';
import {defer, from} from 'rxjs';
import {catchError, concatMap, map, switchMap, tap, toArray} from 'rxjs/operators';
import {
    ApplyJobStatus,
    ApplyMode,
    ApplyPreviewResponse,
    ApplyRequestResponse,
    BaseRule,
    ChangedRuleSummary,
    ChildBucketRule,
    ChildJourneyRule,
    DayOfWeekType,
    ExclusionDate,
    ImpactPreviewResponse,
    ImpactPreviewScopeRequest,
    JourneyDestination,
    JourneyOrigin,
    JourneyType,
    ParentBusinessRule,
    PassengerClass,
    RuleGridEdits,
    RuleDefinitionsResponse,
    JourneyRuleRankingRequest,
    JourneyRuleRankingResponse,
    RulePrioritiesResponse,
    RuleHistoryResponse,
    ValidationFailure,
    ValidationResponse
} from './business-rules.model';
import {LookupService} from '../../shared/lookup.service';
import * as moment from 'moment';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {pipe} from 'rxjs/internal/util/pipe';
import {UrlStore} from '../../shared/url-store';
import {DateUtils} from '../../shared/date-utils';
import {DialogRef, DialogService} from '@progress/kendo-angular-dialog';

const itemIndex = (item: any, data: any[]): number => {
    for (let idx = 0; idx < data.length; idx++) {
        if (data[idx].businessRuleID === item.businessRuleID) {
            return idx;
        }
    }
    return -1;
};

@Injectable({
    providedIn: 'root'
})
export class BusinessRulesService {
    private data: ParentBusinessRule[] = [];
    private cacheData: {[key: string]: any} = {};
    private parentEdits = new RuleGridEdits<ParentBusinessRule>();

    /* Whether this rule has anything staged that the engine has not applied
       yet: the rule itself added or updated in the grid, or any of its child
       rows added, updated or deleted. */
    public hasPendingEdits(businessRuleID: string): boolean {
        const inSet = (edits: {added: any[], updated: any[], deleted: any[]}) =>
            [...edits.added, ...edits.updated, ...edits.deleted]
                .some(item => item.businessRuleID === businessRuleID);
        return inSet(this.parentEdits) || inSet(this.bucketEdits) || inSet(this.journeyEdits);
    }

    // NEW_ placeholder -> the br_id the server assigned, for the save that is
    // currently in flight. Cleared with the edit lists on the next load.
    private newRuleIds = new Map<string, string>();

    private bucketEdits = new RuleGridEdits<ChildBucketRule>();
    private journeyEdits = new RuleGridEdits<ChildJourneyRule>();

    constructor(private lookupService: LookupService, private http: HttpClient, private dialogService: DialogService) {
    }

    public childCountValidationCheck(): ValidationFailure[] {
        const validationFailures = [];
        this.data.forEach(rule => {
            const validationFailure = {
                id: rule.businessRuleID,
                name: rule.name,
                messages: []
            };
            if (rule.expanded) {
                if (rule.bucketRules.length < 1) {
                    validationFailure.messages.push('Missing Bucket Rules');
                }

                if (rule.journeyRules.length < 1) {
                    validationFailure.messages.push('Missing Journey Rules');
                }

                if (validationFailure.messages.length > 0) {
                    validationFailures.push(validationFailure);
                }
            }
        });
        return validationFailures;
    }

    public exportBusinessRules(): Observable<string> {
        return this.http.get<string>(UrlStore.api.businessRules.export);
    }

    public sendConflictReport(): Observable<any> {
        return this.http.post(UrlStore.api.businessRules.conflictReport, {});
    }

    /* Impact preview (RLP-4262). Nothing on this path writes -- the candidate is
       assessed against the live rules and discarded. The candidate is date-
       normalised exactly as validateParentRule does it, since the API receives
       the same ParentBusinessRule shape. `scope` only ever narrows the
       candidate's declared scope, so it is omitted entirely when empty.
       `includeJourneys` pulls the per-journey rows for the Excel export and is
       deliberately opt-in -- the modal itself never needs them. */
    public assessImpact(candidate: ParentBusinessRule,
                        scope?: ImpactPreviewScopeRequest,
                        includeJourneys = false): Observable<ImpactPreviewResponse> {
        const payload: any = {candidate: this.convertStartExpireDates([candidate])[0]};
        if (scope && Object.keys(scope).length > 0) {
            payload.scope = scope;
        }
        if (includeJourneys) {
            payload.include_journeys = true;
        }
        return this.http.post<ImpactPreviewResponse>(UrlStore.api.businessRules.impactPreview, payload);
    }

    public getBuckets(): Observable<any> {
        return this.cachedGet(this.lookupService.loadBuckets(), 'buckets');
    }

    public getBucketRules(businessRuleID: string): Observable<ChildBucketRule[]> {
        return this.http.get<ChildBucketRule[]>(
            UrlStore.api.businessRules.bucketRules,
            {
                params: new HttpParams().set('br_id', businessRuleID)
            });
    }

    /* Read-only edit history for one business rule and every child rule
       hanging off it. Nothing on this path writes. Same `br_id` query
       parameter the bucket and journey child fetches use. */
    public getRuleChangeHistory(businessRuleID: string): Observable<RuleHistoryResponse> {
        return this.http.get<RuleHistoryResponse>(
            UrlStore.api.businessRules.changeHistory,
            {
                params: new HttpParams().set('br_id', businessRuleID)
            });
    }

    public getDaysPrior(): Observable<ExclusionDate[]> {
        return this.cachedGet(
            this.http.get<any[]>(UrlStore.api.businessRules.daysPrior),
            'daysPrior');
    }

    public getDaysOfWeek(): Observable<DayOfWeekType[]> {
        const pipeOptions = [
            switchMap((results: any) => {
                return of(results.data.map(d => new DayOfWeekType(d)));
            })
        ];
        return this.cachedGet(this.lookupService.loadDaysOfWeek(), 'dow', pipeOptions);
    }

    public getDestinations(): Observable<JourneyDestination[]> {
        const pipeOptions = [
            switchMap((results: any) => {
                return of(results.data.map(d => new JourneyDestination(d)));
            })
        ];
        return this.cachedGet(this.lookupService.loadStations(), 'destinations', pipeOptions);
    }

    public getExclusionDates(): Observable<ExclusionDate[]> {
        const cacheKey = 'exclusionDates';
        if (!this.cacheData[cacheKey]) {
            return this.http.get<any[]>(
                UrlStore.api.businessRules.exclusionDates).pipe(
                switchMap(results => {
                    this.cacheData[cacheKey] = results.map(d => {
                        d.name = DateUtils.convertToLocaleDateString(d.name);
                        return new ExclusionDate(d);
                    });
                    return of(this.cacheData[cacheKey]);
                })
            );
        }

        return of(this.cacheData[cacheKey]);
    }

    public getFullParentRule(parentRule: ParentBusinessRule): Observable<ParentBusinessRule> {
        // called when a user attempts to clone a rule that may have not retrieved their child rules.
        if (parentRule.journeyRules.length > 0 && parentRule.bucketRules.length > 0) {
            return of(parentRule);
        }

        const journeyRules = this.getJourneyRules(parentRule.businessRuleID);
        const bucketRules = this.getBucketRules(parentRule.businessRuleID);
        return forkJoin({journeyRules, bucketRules}).pipe(map(results => {
            parentRule.bucketRules = results.bucketRules;
            parentRule.journeyRules = results.journeyRules;
            return parentRule;
        }));
    }

    public getJourneyRules(businessRuleID: string): Observable<ChildJourneyRule[]> {
        return this.http.get<ChildJourneyRule[]>(
            UrlStore.api.businessRules.journeyRules,
            {
                params: new HttpParams().set('br_id', businessRuleID)
            });
    }

    public getJourneyTypes(): Observable<any> {
        const pipeOptions = [
            switchMap((results: any) => {
                return of(results.data.map(d => new JourneyType(d)));
            })
        ];
        return this.cachedGet(this.lookupService.loadJourneyTypes(), 'journeyTypes', pipeOptions);
    }

    public getOrigins(): Observable<JourneyOrigin[]> {
        const pipeOptions = [
            switchMap((results: any) => {
                return of(results.data.map(d => new JourneyOrigin(d)));
            })
        ];
        return this.cachedGet(this.lookupService.loadStations(), 'origins', pipeOptions);
    }

    public getPassengerClasses(): Observable<PassengerClass[]> {
        const pipeOptions = [
            switchMap((results: any) => {
                return of(results.data.map(d => new PassengerClass(d)));
            })
        ];
        return this.cachedGet(this.lookupService.loadClassTypes(), 'paxClasses', pipeOptions);
    }

    public getRSIDs(): Observable<any[]> {
        const pipeOptions = [
            switchMap((results: any) => {
                return of(results.data.map(d => d.id));
            })
        ];
        return this.cachedGet(this.lookupService.loadRSIDsPlus(), 'rsids_plus', pipeOptions);
    }

    public getRules(filterArgs: any): Observable<ParentBusinessRule[]> {
        this.reset();
        return this.http.post<ParentBusinessRule[]>(
            UrlStore.api.businessRules.parentRules,
            filterArgs
        ).pipe(map(res => {
            res.forEach(item => {
                item.startDate = new Date(moment(item.startDate, 'YYYY-MM-DD').utc().toISOString());
                item.expireDate = new Date(moment(item.expireDate, 'YYYY-MM-DD').utc().toISOString());
                return item;
            });
            this.data = res;
            return res;
        }));
    }

    public getRuleNames(brIDs: string[]): string[] {
        if (brIDs === null || brIDs === undefined || brIDs.length === 0) {
            return [];
        }
        const brNames = [];
        this.data.forEach(br => {
            if (brIDs.includes(br.businessRuleID)) {
                brNames.push(br.name);
            }
        });

        return brNames;
    }

    public getRulePriorities(filterArgs: any): Observable<RulePrioritiesResponse> {
        return this.http.post<RulePrioritiesResponse>(
            UrlStore.api.businessRules.rulePriorities,
            filterArgs
        );
    }

    public saveRulePriorities(payload: any): Observable<any> {
        return this.http.post(UrlStore.api.businessRules.saveRulePriorities, payload);
    }

    public getRuleDefinitions(ruleNames: string[]): Observable<RuleDefinitionsResponse> {
        return this.http.post<RuleDefinitionsResponse>(
            UrlStore.api.businessRules.ruleDefinitions,
            {rule_names: ruleNames}
        );
    }

    /**
     * Returns the full ranked business rule contest for one journey: the rules that were
     * applied and the rules a higher-priority rule beat (RLP-4261).
     */
    public getJourneyRuleRanking(journey: JourneyRuleRankingRequest): Observable<JourneyRuleRankingResponse> {
        return this.http.post<JourneyRuleRankingResponse>(
            UrlStore.api.businessRules.journeyRuleRanking,
            journey
        );
    }

    public getRuleTypes(): Observable<any[]> {
        return this.cachedGet(this.http.get(UrlStore.api.businessRules.ruleTypes), 'ruleTypes');
    }

    // One "Save" click can span several independent PATCH/PUT/DELETE calls
    // (bucket/journey/parent_rules, each further split into add/update/delete).
    // These run in sequence rather than in parallel (forkJoin) because two of
    // them can target the same business rule -- e.g. deleting a bucket rule
    // and adding a journey rule to the same rule in one save -- and each one
    // independently checks and bumps that rule's shared version. Run in
    // parallel, whichever request's lock is granted second would see the
    // version the first one just bumped and get a false conflict against the
    // user's own save. Running them in sequence and relaying each response's
    // `versions` into the edit lists before the next step's request body is
    // even built (via defer(), so that build-out happens at subscribe time,
    // not upfront) keeps every later step working off the current version
    // instead of a snapshot taken before the save began.
    public saveGridEdits(): Observable<any> {
        if (!this.hasChanges()) {
            return of(false);
        }

        const steps = [
            ...this.buildBucketEditSteps(),
            ...this.buildJourneyEditSteps(),
            ...this.buildParentEditSteps()
        ];

        const combined: {[key: string]: any} = {};
        return from(steps).pipe(
            concatMap(step => defer(step.run).pipe(map(result => ({key: step.key, result})))),
            tap(({key, result}) => {
                combined[key] = result;
                this.applyUpdatedVersions(result);
            }),
            toArray(),
            map(() => combined)
        );
    }

    // Propagates a successful call's new per-rule version onto every pending
    // edit item (across bucket/journey/parent, add/update/delete) sharing
    // that businessRuleID, so any of this save's later steps that haven't
    // fired yet pick up the current version when their request body is built.
    private applyUpdatedVersions(result: any): void {
        if (!result || result instanceof HttpErrorResponse || !result.versions) {
            return;
        }
        const pendingEditItems: BaseRule[] = [
            ...this.bucketEdits.added, ...this.bucketEdits.updated, ...this.bucketEdits.deleted,
            ...this.journeyEdits.added, ...this.journeyEdits.updated, ...this.journeyEdits.deleted,
            ...this.parentEdits.added, ...this.parentEdits.updated, ...this.parentEdits.deleted
        ];
        pendingEditItems.forEach(item => {
            const newVersion = result.versions[item.businessRuleID];
            if (newVersion !== undefined) {
                item.lastUpdatedDate = newVersion;
            }
        });
    }

    private reset() {
        this.newRuleIds.clear();
        this.parentEdits.reset();
        this.bucketEdits.reset();
        this.journeyEdits.reset();
    }

    public removeRule(item: any, ruleType: any): void {
        if (ruleType === ChildBucketRule) {
            this.removeBucketRule(item);
        } else if (ruleType === ChildJourneyRule) {
            this.removeJourneyRule(item);
        } else if (ruleType === ParentBusinessRule) {
            this.removeParentRule(item);
        } else {
            console.error('Unable to remove rule');
        }
    }

    public saveRule(dataItem: any, ruleType: any, isNew: boolean): void {
        if (ruleType === ChildBucketRule) {
            this.saveBucketRule(dataItem, isNew);
        } else if (ruleType === ChildJourneyRule) {
            this.saveJourneyRule(dataItem, isNew);
        } else if (ruleType === ParentBusinessRule) {
            this.saveParentRule(dataItem, isNew);
        }
    }

    public update(item: any, ruleType: any): void {
        if (ruleType === ChildBucketRule) {
            this.bucketEdits.update(item);
        } else if (ruleType === ChildJourneyRule) {
            this.journeyEdits.update(item);
        } else if (ruleType === ParentBusinessRule) {
            this.journeyEdits.update(item);
        } else {
            console.error('Unable to update rule');
        }
    }

    public hasChanges(): boolean {
        return this.parentEdits.hasChanges() || this.bucketEdits.hasChanges() || this.journeyEdits.hasChanges();
    }

    // Parent rules carry their own name, so a newly-added one (not yet in the
    // grid's loaded data, since it doesn't exist server-side until this save)
    // can be named directly rather than resolved by ID. parentEdits is private
    // and only gets reset() on the next grid reload (see getRules()), so this
    // is safe to read right after saveGridEdits()'s observable resolves and
    // before that reload runs.
    /* ---- Immediate apply of saved rules (RLP-4281) ------------------------ */

    /* Every rule this save is about to touch, for the Save and Apply checklist.

       Built from all three edit collections because a rule can be changed entirely
       through its children -- adding a bucket rule is a change to the rule it belongs
       to. Deletions are included on purpose: applying a deleted rule immediately is how
       its effect gets unwound before tonight's run would do it.

       Must be called BEFORE saveGridEdits(), while the edit lists still describe the
       pending work; the ids of brand-new rules are resolved afterwards by
       resolveSavedRuleId(). */
    public getChangedRuleSummaries(): ChangedRuleSummary[] {
        const summaries = new Map<string, ChangedRuleSummary>();
        const collect = (items: BaseRule[]) => {
            items.forEach(item => {
                const businessRuleID = item.businessRuleID;
                if (!businessRuleID || summaries.has(businessRuleID)) {
                    return;
                }
                summaries.set(businessRuleID, {
                    businessRuleID,
                    name: this.resolveChangedRuleName(businessRuleID),
                    isNew: businessRuleID.startsWith('NEW_'),
                    selected: true
                });
            });
        };
        [this.parentEdits, this.bucketEdits, this.journeyEdits].forEach(edits => {
            collect(edits.added);
            collect(edits.updated);
            collect(edits.deleted);
        });
        return [...summaries.values()];
    }

    /* The server id for a rule that carried a NEW_ placeholder when the checklist was
       built. Returns null while the save has not produced one -- the caller must drop
       that rule rather than send a placeholder the API will reject. */
    public resolveSavedRuleId(businessRuleID: string): string | null {
        if (!businessRuleID) {
            return null;
        }
        if (!businessRuleID.startsWith('NEW_')) {
            return businessRuleID;
        }
        return this.newRuleIds.get(businessRuleID) || null;
    }

    /* Throws away everything staged in the grid. The caller reloads afterwards: the
       add paths mutate the loaded rule list in place, so clearing the edit lists alone
       would leave those rows on screen with nothing backing them. */
    public discardPendingEdits(): void {
        this.reset();
    }

    /* Coarse magnitude of an immediate apply, for the modal.

       Sends the full candidate rules rather than their ids because the engine's rule
       matview is only refreshed overnight -- a rule saved seconds ago is not in it, so
       the server has to work from the rule's own definition. */
    public getApplyPreview(candidates: ParentBusinessRule[],
                           startDate: string,
                           endDate: string): Observable<ApplyPreviewResponse> {
        return this.http.post<ApplyPreviewResponse>(
            UrlStore.api.businessRules.applyPreview,
            {candidates, scope: {start_date: startDate, end_date: endDate}}
        );
    }

    /* The same estimate addressed by rule id. The rules are saved by the time the apply
       modal opens, so their full definitions -- which the estimate needs, the rule
       matview not having been refreshed yet -- can be fetched from the server. A rule
       whose definition cannot be loaded is left out rather than failing the estimate. */
    public getApplyPreviewForRules(brIds: string[],
                                   startDate: string,
                                   endDate: string): Observable<ApplyPreviewResponse> {
        const lookups = brIds.map(businessRuleID => {
            const loaded = this.data.find(rule => rule.businessRuleID === businessRuleID);
            const stub = loaded || ({businessRuleID, bucketRules: [], journeyRules: []} as any);
            return this.getFullParentRule(stub).pipe(catchError(() => of(null)));
        });
        if (lookups.length === 0) {
            return of({journeys: 0, ods: 0, departure_dates: 0, buckets: 0, rules: 0});
        }
        return forkJoin(lookups).pipe(
            switchMap((rules: ParentBusinessRule[]) =>
                this.getApplyPreview(rules.filter(rule => !!rule), startDate, endDate))
        );
    }

    public requestApply(brIds: string[],
                        startDate: string,
                        endDate: string,
                        mode: ApplyMode): Observable<ApplyRequestResponse> {
        return this.http.post<ApplyRequestResponse>(
            UrlStore.api.businessRules.apply,
            {brIds, startDate, endDate, mode}
        );
    }

    public getApplyJob(applyId: string): Observable<ApplyJobStatus> {
        return this.http.get<ApplyJobStatus>(`${UrlStore.api.businessRules.apply}/${applyId}`);
    }

    /* A parent edit carries its own name; a rule reached only through a child row does
       not, so it is looked up in the loaded grid data. */
    private resolveChangedRuleName(businessRuleID: string): string {
        const pending = [...this.parentEdits.added, ...this.parentEdits.updated,
            ...this.parentEdits.deleted].find(rule => rule.businessRuleID === businessRuleID);
        if (pending && pending.name) {
            return pending.name;
        }
        const loaded = this.data.find(rule => rule.businessRuleID === businessRuleID);
        return loaded ? loaded.name : businessRuleID;
    }

    public getPendingParentRuleNames(): {addedParent: string[], deletedParent: string[]} {
        return {
            addedParent: this.parentEdits.added.map(rule => rule.name),
            deletedParent: this.parentEdits.deleted.map(rule => rule.name)
        };
    }

    public validateBucketRule(currentRules: any, changedRule: any, parentRule: ParentBusinessRule): Observable<ValidationResponse> {
        const parentValidation = this.validateParentRule(parentRule);
        const bucketValidation = this.http.post<ValidationResponse>(UrlStore.api.businessRules.bucketValidation,
            {currentRules, changedRule});
        return forkJoin({bucketValidation, parentValidation}).pipe(map(resp => {
            if (!resp.parentValidation.isValid) {
                return resp.parentValidation;
            }
            return resp.bucketValidation;
        }));
    }

    public validateClonedRules(): Observable<ValidationResponse> {
        let clonedRules = this.parentEdits.added.filter(br => br.changeType === 'clone');
        if (clonedRules.length > 0) {
            const changedRules = this.convertStartExpireDates(
                this.getChangedRules(clonedRules.map(br => br.businessRuleID)));
            clonedRules = this.convertStartExpireDates(clonedRules);
            return this.validateParentRules(changedRules, clonedRules);
        } else {
            return of({isValid: true, pass: [], fail: [], overlapALLtoALLRules: []});
        }
    }

    public validateJourneyRule(ruleToValidate: ParentBusinessRule): Observable<ValidationResponse> {
        return this.validateParentRule(ruleToValidate);
    }

    public validateParentRule(ruleToValidate: ParentBusinessRule): Observable<ValidationResponse> {
        // create list of existing rules
        const changedRules = this.getChangedRules([ruleToValidate.businessRuleID]);
        ruleToValidate = this.convertStartExpireDates([ruleToValidate])[0];
        return this.validateParentRules(changedRules, [ruleToValidate]);
    }

    // Each step's `run` is only invoked at subscribe time (via defer() in
    // saveGridEdits()), which is what lets a later step pick up a version
    // bumped by an earlier one in the same save -- building the request body
    // eagerly here (as this used to) would snapshot lastUpdatedDate before
    // any step had a chance to run.
    private buildBucketEditSteps(): {key: string, run: () => Observable<any>}[] {
        const steps: {key: string, run: () => Observable<any>}[] = [];
        if (this.bucketEdits.added.length > 0) {
            steps.push({
                key: 'addedBucket',
                run: () => this.saveNewBucketRules(this.bucketEdits.added).pipe(catchError(error => of(error)))
            });
        }
        if (this.bucketEdits.updated.length > 0) {
            steps.push({
                key: 'updatedBucket',
                run: () => this.saveUpdatedBucketRules(this.bucketEdits.updated).pipe(catchError(error => of(error)))
            });
        }
        if (this.bucketEdits.deleted.length > 0) {
            steps.push({
                key: 'deletedBucket',
                run: () => this.saveDeletedBucketRules(this.bucketEdits.deleted).pipe(catchError(error => of(error)))
            });
        }
        return steps;
    }

    private buildJourneyEditSteps(): {key: string, run: () => Observable<any>}[] {
        const steps: {key: string, run: () => Observable<any>}[] = [];
        if (this.journeyEdits.added.length > 0) {
            steps.push({
                key: 'addedJourney',
                run: () => this.saveNewJourneyRules(this.journeyEdits.added).pipe(catchError(error => of(error)))
            });
        }
        if (this.journeyEdits.updated.length > 0) {
            steps.push({
                key: 'updatedJourney',
                run: () => this.saveUpdatedJourneyRules(this.journeyEdits.updated).pipe(catchError(error => of(error)))
            });
        }
        if (this.journeyEdits.deleted.length > 0) {
            steps.push({
                key: 'deletedJourney',
                run: () => this.saveDeletedJourneyRules(this.journeyEdits.deleted).pipe(catchError(error => of(error)))
            });
        }
        return steps;
    }

    private buildParentEditSteps(): {key: string, run: () => Observable<any>}[] {
        const steps: {key: string, run: () => Observable<any>}[] = [];
        if (this.parentEdits.added.length > 0) {
            steps.push({
                key: 'addedParent',
                run: () => this.saveNewParentRule(this.convertStartExpireDates(this.parentEdits.added))
                    .pipe(catchError(error => of(error)))
            });
        }
        if (this.parentEdits.updated.length > 0) {
            steps.push({
                key: 'updatedParent',
                run: () => this.saveUpdatedParentRules(this.convertStartExpireDates(this.parentEdits.updated))
                    .pipe(catchError(error => of(error)))
            });
        }
        if (this.parentEdits.deleted.length > 0) {
            steps.push({
                key: 'deletedParent',
                run: () => this.saveDeletedParentRules(this.parentEdits.deleted).pipe(catchError(error => of(error)))
            });
        }
        return steps;
    }

    // Converts to start and expire dates in a list of parent rules to string values.
    // Used before sending rules to the API.
    private convertStartExpireDates(parentRules: ParentBusinessRule[]): ParentBusinessRule[] {
        return parentRules.map(rule => {
            const clonedRule = Object.assign({}, rule);
            clonedRule.startDate = moment(clonedRule.startDate).format('YYYY-MM-DD');
            clonedRule.expireDate = moment(clonedRule.expireDate).format('YYYY-MM-DD');
            return clonedRule;
        });
    }

    private createBucketRule(bucketRule: ChildBucketRule): void {
        if (this.data.find(d => d.businessRuleID === bucketRule.businessRuleID).bucketRules.find(d =>
            ((d.businessRuleID === bucketRule.businessRuleID) && (d.class === bucketRule.class) &&
                (d.bucketCode === bucketRule.bucketCode) && (d.rule === bucketRule.rule)  &&
                (d.rule !== 'Min_Alloc') && (d.rule !== 'Max_Alloc')))) {
            const alertString = 'The Bucket rule for '  +  bucketRule.class.charAt(0).toUpperCase() +
                bucketRule.class.slice(1).toLowerCase() + '-' + bucketRule.bucketCode + '-' + bucketRule.rule + ' is already present.';
            const dialog: DialogRef = this.dialogService.open({
            title: 'Duplicate Bucket Rule!!!',
            content: alertString,
            actions: [
                {text: 'OK', primary: true}
            ]
        });
        } else {
            this.data.find(d => d.businessRuleID === bucketRule.businessRuleID).bucketRules.unshift(bucketRule);
            // Only need to add the bucket rule to list if the parent business rule is not new
            // Bucket rules of new parent rules get saved during the save new parent rule process.
            if (!bucketRule.businessRuleID.startsWith('NEW_')) {
                this.bucketEdits.add(bucketRule);
            }
        }
    }

    private createJourneyRule(journeyRule: ChildJourneyRule): void {
        if ((journeyRule.origin === journeyRule.destination) && (journeyRule.origin != '*ALL') && (journeyRule.destination != '*ALL')) {
            const alertString = 'The Journey Rule has same Origin and Destination: '  +  journeyRule.origin + '-' + journeyRule.destination;
            const dialog: DialogRef = this.dialogService.open({
            title: 'Invalid Journey Rule!!!',
            content: alertString,
            actions: [
                {text: 'OK', primary: true}
            ]
        });
            return;
        } else if (this.data.find(d => d.businessRuleID === journeyRule.businessRuleID).journeyRules.find(d =>
            ((d.businessRuleID === journeyRule.businessRuleID) && (d.origin === journeyRule.origin) &&
                (d.destination === journeyRule.destination) ))) {
            const alertString = 'The Journey Rule for '  +  journeyRule.origin + '-' + journeyRule.destination + ' is already present.';
            const dialog: DialogRef = this.dialogService.open({
            title: 'Duplicate Journey Rule!!!',
            content: alertString,
            actions: [
                {text: 'OK', primary: true}
            ]
        });

        } else {
            this.data.find(d => d.businessRuleID === journeyRule.businessRuleID).journeyRules.unshift(journeyRule);
            // Only need to add the journey rule to list if the parent business rule is not new
            // Journey rules of new parent rules get saved during the save new parent rule process.
            if (!journeyRule.businessRuleID.startsWith('NEW_')) {
                this.journeyEdits.add(journeyRule);
            }
        }
    }

    async journeyRuleValidation(journeyRule: ChildJourneyRule): Promise<void> {
        let journeyRules = await this.getJourneyRules(journeyRule.businessRuleID).toPromise();
        if ((journeyRule.origin === journeyRule.destination) && (journeyRule.origin != '*ALL') && (journeyRule.destination != '*ALL')) {
            const alertString = 'The Journey Rule has same Origin and Destination: ' + journeyRule.origin + '-' + journeyRule.destination;
            const dialog: DialogRef = this.dialogService.open({
                title: 'Invalid Journey Rule!!!',
                content: alertString,
                actions: [
                    {text: 'OK', primary: true}
                ]
            });
            return;
        } else if (journeyRules.find(d => ((d.businessRuleID === journeyRule.businessRuleID) && (d.origin === journeyRule.origin) &&
                (d.destination === journeyRule.destination) && (d.rsid === journeyRule.rsid)))) {
            console.log(journeyRule.businessRuleID);
            console.log(journeyRule.rsid);
            const alertString = 'The Journey Rule for ' + journeyRule.origin + '-' + journeyRule.destination + ' is already present.';
            const dialog: DialogRef = this.dialogService.open({
                title: 'Duplicate Journey Rule!!!',
                content: alertString,
                actions: [
                    {text: 'OK', primary: true}
                ]
            });
            return;
        } else {
            this.journeyEdits.update(journeyRule);
        }
    }

    private createParentRule(parentRule: ParentBusinessRule): void {
        this.data.unshift(parentRule);
        this.parentEdits.add(parentRule);
    }

    // Returns a list of all business rules that have changed, excluding the provided business rule IDs.
    private getChangedRules(excludeIDs: string[]): ParentBusinessRule[] {
        const parentChanges = this.parentEdits.getChangedBusinessRuleIDs(excludeIDs);
        const journeyChanges = this.journeyEdits.getChangedBusinessRuleIDs(excludeIDs);
        const bucketChanges = this.bucketEdits.getChangedBusinessRuleIDs(excludeIDs);
        const setOfBrIDs = new Set([...parentChanges, ...journeyChanges, ...bucketChanges]);
        return this.convertStartExpireDates(this.data.filter(br => setOfBrIDs.has(br.businessRuleID)));
    }

    private removeBucketRule(bucketRule: ChildBucketRule): void {
        const parentRule = this.data.find(d => d.businessRuleID === bucketRule.businessRuleID);
        const index = parentRule.bucketRules.indexOf(bucketRule);
        parentRule.bucketRules.splice(index, 1);
        this.bucketEdits.delete(bucketRule);
    }

    private removeJourneyRule(journeyRule: ChildJourneyRule): void {
        const parentRule = this.data.find(d => d.businessRuleID === journeyRule.businessRuleID);
        const index = parentRule.journeyRules.indexOf(journeyRule);
        parentRule.journeyRules.splice(index, 1);
        this.journeyEdits.delete(journeyRule);
    }

    private removeParentRule(parentRule: ParentBusinessRule): void {
        const index = itemIndex(parentRule, this.data);
        if (index >= 0) {
            this.data.splice(index, 1);
        }
        this.parentEdits.delete(parentRule);
    }

    private saveBucketRule(bucketRule: ChildBucketRule, isNew: boolean): void {
        if (isNew) {
            this.createBucketRule(bucketRule);
        } else if (!bucketRule.businessRuleID.startsWith('NEW_')) {
            this.bucketRuleEditValidation(bucketRule);
        }
    }

    async bucketRuleEditValidation(bucketRule: ChildBucketRule): Promise<void> {
        let bucketRules = await this.getBucketRules(bucketRule.businessRuleID).toPromise();
        if (bucketRules.find(d =>
            ((d.businessRuleID === bucketRule.businessRuleID) && (d.class === bucketRule.class) &&
                (d.bucketCode === bucketRule.bucketCode) && (d.rule === bucketRule.rule) &&
                (d.minAlloc === bucketRule.minAlloc) && (d.maxAlloc === bucketRule.maxAlloc) &&
                (d.rule !== 'Min_Alloc') && (d.rule !== 'Max_Alloc') &&
                (d.daysPrior === bucketRule.daysPrior)))) {
            console.log(bucketRule);
            const alertString = 'The Bucket rule for ' + bucketRule.class.charAt(0).toUpperCase() +
                bucketRule.class.slice(1).toLowerCase() + '-' + bucketRule.bucketCode + '-' + bucketRule.rule + ' is already present.';
            const dialog: DialogRef = this.dialogService.open({
                title: 'Duplicate Bucket Rule!!!',
                content: alertString,
                actions: [
                    {text: 'OK', primary: true}
                ]
            });
        } else {
            this.bucketEdits.update(bucketRule);
        }
    }

    private saveDeletedBucketRules(bucketRules: ChildBucketRule[]): Observable<any> {
        // Deleting a bucket rule is still a change to the rule it belongs to,
        // so the server checks it against the parent's version the same way
        // an update/add is — it needs businessRuleID + lastUpdatedDate per row,
        // not just the id.
        const rules = bucketRules.map(br => ({
            id: br.id,
            businessRuleID: br.businessRuleID,
            lastUpdatedDate: br.lastUpdatedDate
        }));
        return this.saveDeletedRulesWithVersionCheck(rules, 'buckets');
    }

    private saveDeletedJourneyRules(rules: ChildJourneyRule[]): Observable<any> {
        // One UI journey rule row can span several underlying OD rows (one per
        // rsid) — each carries the enclosing rule's businessRuleID/lastUpdatedDate
        // for the same reason as saveDeletedBucketRules above.
        const odRows = [].concat(...rules.map(br => br.rowIdRsid.map(row => ({
            id: row.id,
            businessRuleID: br.businessRuleID,
            lastUpdatedDate: br.lastUpdatedDate
        }))));
        return this.saveDeletedRulesWithVersionCheck(odRows, 'journeys');
    }

    private saveDeletedParentRules(rules: ParentBusinessRule[]): Observable<any> {
        return this.saveDeletedRules(rules.map(br => br.businessRuleID), 'parent_rules');
    }

    private saveDeletedRules(ruleIDs: string[], endpoint: string): Observable<any> {
        return this.http.delete(`${UrlStore.api.businessRules.base}/${endpoint}`,
            {
                params: {ids: ruleIDs}
            });
    }

    private saveDeletedRulesWithVersionCheck(
        rules: {id: string, businessRuleID: string, lastUpdatedDate: string}[], endpoint: string
    ): Observable<any> {
        return this.http.delete(`${UrlStore.api.businessRules.base}/${endpoint}`,
            {
                body: rules
            });
    }

    private saveJourneyRule(journeyRule: ChildJourneyRule, isNew: boolean): void {
        if (isNew) {
            this.createJourneyRule(journeyRule);
        } else {
            this.journeyRuleValidation(journeyRule);
        }
    }

    private saveNewBucketRules(bucketRules: ChildBucketRule[]): Observable<any> {
        return this.http.put(UrlStore.api.businessRules.bucketRules,
            bucketRules);
    }

    private saveNewJourneyRules(journeyRules: ChildJourneyRule[]): Observable<any> {
        return this.http.put(UrlStore.api.businessRules.journeyRules,
            journeyRules);
    }

    /* The server answers with the new rules' br_ids, positionally aligned with the
       rules that were sent. They used to be discarded (switchMap over an array emits
       each id separately and the last one won), which left a brand-new rule with only
       its client-side NEW_ placeholder until the grid was reloaded. RLP-4281 needs the
       real id immediately -- an apply keyed on a placeholder applies nothing -- so the
       array is kept whole and the ids are recorded against the placeholders. */
    private saveNewParentRule(newRules: ParentBusinessRule[]): Observable<any> {
        const placeholders = newRules.map(rule => rule.businessRuleID);
        return this.http.put(UrlStore.api.businessRules.parentRules, newRules).pipe(
            tap(result => this.recordNewRuleIds(placeholders, result))
        );
    }

    /* Maps this save's NEW_ placeholders onto the ids the server assigned. */
    private recordNewRuleIds(placeholders: string[], result: any): void {
        if (!Array.isArray(result)) {
            return;
        }
        placeholders.forEach((placeholder, index) => {
            const savedId = result[index];
            if (placeholder && typeof savedId === 'string') {
                this.newRuleIds.set(placeholder, savedId);
            }
        });
    }

    private saveParentRule(parentRule: ParentBusinessRule, isNew: boolean): void {
        if (isNew) {
            this.createParentRule(parentRule);
        } else {
            this.parentEdits.update(parentRule);
        }
    }

    private saveUpdatedBucketRules(bucketRules: ChildBucketRule[]): Observable<any> {
        return this.http.patch(UrlStore.api.businessRules.bucketRules,
            bucketRules);
    }

    private saveUpdatedJourneyRules(rules: ChildJourneyRule[]): Observable<any> {
        return this.http.patch(UrlStore.api.businessRules.journeyRules,
            rules);
    }

    private saveUpdatedParentRules(rules: ParentBusinessRule[]): Observable<any> {
        // clear out bucket and journey rules
        rules.forEach(rule => {
            rule.bucketRules = [];
            rule.journeyRules = [];
        });
        return this.http.patch(UrlStore.api.businessRules.parentRules,
            rules);
    }

    private validateParentRules(changedRules: ParentBusinessRule[], rulesToValidate: ParentBusinessRule[]): Observable<ValidationResponse> {
        return this.http.post<ValidationResponse>(UrlStore.api.businessRules.parentValidation,
            {changedRules, rulesToValidate});
    }

    private cachedGet(obs: Observable<any>, cacheKey: string, pipeOptions: any[] = []): Observable<any> {
        if (!this.cacheData[cacheKey]) {
            const cachedOperator = switchMap((result: any) => {
                this.cacheData[cacheKey] = result;
                return of(this.cacheData[cacheKey]);
            });
            const pipeOperators = [...pipeOptions, cachedOperator];
            return obs.pipe(pipe.apply(this, pipeOperators));
        }
        return of(this.cacheData[cacheKey]);
    }

}
